import { useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * The official HLSC mark — a faithful vector reproduction, not a
 * reinterpretation.
 *
 * The client supplies this mark only as raster
 * (`public/brand/mark.png`, an unmodified copy of their own
 * `HLSC_Logo_Lato_hoch_logo.png`, 472x345, kept in the repo as the
 * archival source of truth — re-derive or re-check this file against it,
 * not against memory of what a logo "usually" looks like). A raster image
 * can't animate, can't stay crisp at arbitrary sizes, and can't sit inside
 * an SVG-based entrance sequence, so this component transcribes that
 * artwork into vector at the exact geometry measured from it: same 4x3
 * grid, same 12 node positions, same fills in the same places, same heavy
 * black node outlines, same connecting lines including every diagonal,
 * same proportions. Nothing here is invented, simplified, or rearranged.
 *
 * The geometry below (a 472x345 coordinate space, matching the source
 * PNG's own pixel dimensions) was measured directly from that file:
 * connected-component analysis on the fill colours located all 12 node
 * centres and their radius; the outline width was recovered from the gap
 * between each node's coloured-fill radius and its full opaque radius;
 * grid spacing was averaged across all three row gaps and all three
 * column gaps. Rendered at 472x345 and diffed pixel-for-pixel against the
 * source: away from anti-aliased edges (~75% of the image), the mean
 * channel difference is 0.87/255 with no pixel differing by more than
 * 17/255 — the two are the same drawing. All remaining difference is
 * confined to ~1px-wide edge halos, the expected artefact of two different
 * rasterisers anti-aliasing the same vector differently, not a geometry
 * mismatch. Full comparison images and the measurement script are
 * described in this change's report; re-run them before changing any
 * number below.
 *
 * Because the strokes and radius are defined in this same coordinate
 * space, scaling the whole `<svg>` by `size` scales them with it — a
 * render at 24px has proportionally the same line weight as one at 240px,
 * which is what actually reproducing artwork at a different size means.
 * The old version of this file instead held stroke width constant in
 * screen pixels regardless of render size, which quietly fattens the
 * relative line weight the smaller the mark gets — itself a departure from
 * the source proportions. It doesn't do that any more.
 *
 * --- The dark-background problem, revisited ---------------------------
 * The artwork's outlines were drawn permanently black, for a white page.
 * The brand guidelines' own variation list names three official treatments:
 * a full-colour version for light backgrounds, a monochrome dark version
 * also for light backgrounds, and "a reversed white version (on dark/
 * coloured backgrounds)" — so a reversed treatment for dark surfaces is a
 * documented part of the identity, not an invention. The client does not
 * hold that file, so what follows is this component's reconstruction of it
 * from the same measured geometry, not a copy of an official reversed
 * master — a designer should confirm it before it is treated as final.
 *
 * The reconstruction changes exactly one thing: the black line-work (the
 * node outlines and every connecting line) switches to a light ink via
 * `--mark-outline`, which is unset (falling back to the same literal
 * `#000000` as before) everywhere except the three contexts that are
 * actually dark — `.stage`, the transparent header over the hero, and the
 * app shell's own dark theme — where logo.css sets it to a light value.
 * Every node keeps its own measured brand fill (see NODES below) rather
 * than being flattened to a white silhouette: checked against the stage's
 * night-chart background, every one of those fills — including the two
 * yellow corner nodes the brand reserves for verified evidence — already
 * clears a comfortable contrast on its own, and the lattice reads as the
 * same full-colour mark with its ink reversed, not a different logo. Node
 * position, count, radius and every connecting line are untouched; there is
 * no plate, no background panel, and nothing sits between the mark and the
 * page any more.
 *
 * --- Motion -----------------------------------------------------------
 * `animate` plays once immediately (on mount); `animateOnView` instead
 * waits until the mark actually scrolls into the viewport before playing
 * once — for an instance placed below the fold, where "on mount" would
 * finish before anyone is looking at it. Either way: each connecting line
 * draws from one node to the other (stroke-dashoffset, not a clip or a
 * wipe — nothing is ever scaled, stretched or repositioned to do this),
 * brightening through the brand's signal colour (`--logo-signal`) as it
 * draws before settling to the resting ink — a network of evidence
 * resolving into order, and registering, not a decorative flourish — and
 * each node settles in shortly after the lines that meet it arrive. It
 * runs exactly once per mount, never loops, uses the same ease-out the
 * rest of the interface uses (`--ease-out`), and always settles to the
 * exact resting artwork above: the animation is a reveal of the faithful
 * drawing, never a distortion of it. `prefers-reduced-motion` shows the
 * resting state immediately and never starts an observer — see the media
 * query in logo.css.
 */

// Exported so the marketing site's background lattice
// (web/src/site/LatticeField.tsx) can derive its own grid pitch from these
// same numbers instead of an unrelated constant — the field and the mark
// are then provably the same lattice, not two coincidentally similar ones.
export const VIEW_WIDTH = 472;
export const VIEW_HEIGHT = 345;
export const PAD_X = 60.51;
export const PAD_Y = 49.91;
export const STEP_X = 118.45;
export const STEP_Y = 118.1;
export const RADIUS = 30.5;
const NODE_STROKE = 4;
const LINE_STROKE = 2.5;
// The official artwork's line-work is black; `--mark-outline` lets a dark
// surface reverse it to a light ink instead (see the file comment above).
// Unset anywhere else, so it falls back to this exact literal.
const OUTLINE = 'var(--mark-outline, #000000)';

type NodeSpec = { c: number; r: number; fill: string; ignite?: boolean };

// Fill assignment measured from the source artwork: yellow at both top
// corners, the teal hub at row 1 / column 2, grey-slate at row 1 / column
// 0, dark-slate at row 2 / column 1, pale light-blue at row 2 / column 3,
// HLSC blue everywhere else. The two yellow corners are the brand's
// verified-evidence colour, so they alone carry `ignite`: the concept's
// extra bloom once the reveal has otherwise settled (see the file comment).
const NODES: NodeSpec[] = [
  { c: 0, r: 0, fill: 'var(--brand-yellow)', ignite: true },
  { c: 1, r: 0, fill: 'var(--brand-blue)' },
  { c: 2, r: 0, fill: 'var(--brand-blue)' },
  { c: 3, r: 0, fill: 'var(--brand-yellow)', ignite: true },
  { c: 0, r: 1, fill: 'var(--brand-grey)' },
  { c: 1, r: 1, fill: 'var(--brand-blue)' },
  { c: 2, r: 1, fill: 'var(--brand-teal)' },
  { c: 3, r: 1, fill: 'var(--brand-blue)' },
  { c: 0, r: 2, fill: 'var(--brand-blue)' },
  { c: 1, r: 2, fill: 'var(--brand-slate-dark)' },
  { c: 2, r: 2, fill: 'var(--brand-blue)' },
  { c: 3, r: 2, fill: 'var(--brand-light-blue)' },
];

const px = (c: number) => PAD_X + c * STEP_X;
const py = (r: number) => PAD_Y + r * STEP_Y;

type Edge = { x1: number; y1: number; x2: number; y2: number };
// Half of an edge, from its own midpoint out to one endpoint, carrying the
// parent edge's stagger index so both halves of the same edge always draw
// together — see buildHalfEdges.
type HalfEdge = Edge & { i: number };

function buildEdges(): Edge[] {
  const out: Edge[] = [];
  // Horizontals and verticals.
  for (let r = 0; r < 3; r += 1) {
    for (let c = 0; c < 3; c += 1) out.push({ x1: px(c), y1: py(r), x2: px(c + 1), y2: py(r) });
  }
  for (let c = 0; c < 4; c += 1) {
    for (let r = 0; r < 2; r += 1) out.push({ x1: px(c), y1: py(r), x2: px(c), y2: py(r + 1) });
  }
  // Every cell diagonalled, both ways — this is a defining feature of the
  // source artwork, not decoration to thin out.
  for (let r = 0; r < 2; r += 1) {
    for (let c = 0; c < 3; c += 1) {
      out.push({ x1: px(c), y1: py(r), x2: px(c + 1), y2: py(r + 1) });
      out.push({ x1: px(c + 1), y1: py(r), x2: px(c), y2: py(r + 1) });
    }
  }
  return out;
}

const EDGES = buildEdges();

/**
 * Each edge split into two half-segments from its own midpoint out to each
 * endpoint — how design-concept-v2's approved reveal draws a connection
 * "stroking outward from centre" rather than sweeping from one end to the
 * other. Each half keeps `pathLength={1}` on its own half-length, so the
 * existing stroke-dasharray/offset keyframe (logo.css's `mark-line`) needs
 * no change: both halves just animate on the same delay, in sync.
 */
function buildHalfEdges(edges: Edge[]): HalfEdge[] {
  const out: HalfEdge[] = [];
  edges.forEach((e, i) => {
    const mx = (e.x1 + e.x2) / 2;
    const my = (e.y1 + e.y2) / 2;
    out.push({ x1: mx, y1: my, x2: e.x1, y2: e.y1, i });
    out.push({ x1: mx, y1: my, x2: e.x2, y2: e.y2, i });
  });
  return out;
}

const HALF_EDGES = buildHalfEdges(EDGES);

export type MarkProps = {
  size?: number;
  /** Plays the reveal immediately on mount — see the file comment. Resolves
   * to the exact resting artwork; disabled entirely under reduced motion. */
  animate?: boolean;
  /** Plays the same reveal once, but only once the mark has actually
   * scrolled into view, for an instance placed below the fold — see the
   * file comment. Ignored if `animate` is also set. */
  animateOnView?: boolean;
  className?: string;
  title?: string;
};

export function Mark({ size = 40, animate = false, animateOnView = false, className, title }: MarkProps) {
  const height = Math.round((size * VIEW_HEIGHT) / VIEW_WIDTH);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!animateOnView || animate || inView) return undefined;
    if (typeof window === 'undefined' || typeof IntersectionObserver !== 'function') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const el = svgRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [animateOnView, animate, inView]);

  const draw = animate || (animateOnView && inView);

  return (
    <span className={['mark-plate', className || ''].filter(Boolean).join(' ')}>
      <svg
        ref={svgRef}
        className={['mark', draw ? 'mark-draw' : ''].filter(Boolean).join(' ')}
        width={size}
        height={height}
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        shapeRendering="geometricPrecision"
        role={title ? 'img' : 'presentation'}
        aria-label={title}
        aria-hidden={title ? undefined : true}
      >
        <g className="mark-edges" stroke={OUTLINE} strokeWidth={LINE_STROKE} strokeLinecap="round">
          {HALF_EDGES.map((e, k) => (
            <line
              key={`e${k}`}
              x1={e.x1}
              y1={e.y1}
              x2={e.x2}
              y2={e.y2}
              pathLength={1}
              style={{ '--i': e.i } as CSSProperties}
            />
          ))}
        </g>
        <g className="mark-nodes">
          {NODES.map((node, i) => (
            <circle
              key={`n${i}`}
              className={['mark-node', node.ignite ? 'mark-node-ignite' : ''].filter(Boolean).join(' ')}
              cx={px(node.c)}
              cy={py(node.r)}
              r={RADIUS}
              fill={node.fill}
              stroke={OUTLINE}
              strokeWidth={NODE_STROKE}
              style={{ '--i': i } as CSSProperties}
            />
          ))}
        </g>
      </svg>
    </span>
  );
}
